/**
 * Social media links for a congregation (F3): `church.socials` stores canonical
 * `{ whatsapp?, facebook?, youtube?, instagram? }`. This helper tolerates
 * format (free text with URL) and normalizes to typed list to
 * render clickably on public page and card.
 */

export type SocialKind = "whatsapp" | "facebook" | "youtube" | "instagram" | "other";

export interface SocialLink {
  kind: SocialKind;
  url: string;
}

const KNOWN_KINDS: readonly SocialKind[] = ["whatsapp", "facebook", "youtube", "instagram"];

function isKnownKind(value: string): value is SocialKind {
  return (KNOWN_KINDS as readonly string[]).includes(value);
}

function kindFromUrl(url: string): SocialKind {
  const lower = url.toLowerCase();
  if (lower.includes("wa.me") || lower.includes("whatsapp.com")) {
    return "whatsapp";
  }
  if (lower.includes("facebook.com") || lower.includes("fb.com") || lower.includes("fb.watch")) {
    return "facebook";
  }
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
    return "youtube";
  }
  if (lower.includes("instagram.com")) {
    return "instagram";
  }
  return "other";
}

/** http(s) only: stored link never renders with another scheme. */
function isSafeUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Parses `church.socials` (canonical JSON or legacy text) to safe links. */
export function parseSocialLinks(raw: string | null | undefined): SocialLink[] {
  const value = raw?.trim();
  if (!value) {
    return [];
  }
  if (value.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed && typeof parsed === "object") {
        const links: SocialLink[] = [];
        for (const [key, url] of Object.entries(parsed)) {
          if (typeof url !== "string" || !isSafeUrl(url.trim())) {
            continue;
          }
          const kind = isKnownKind(key) ? key : kindFromUrl(url);
          links.push({ kind, url: url.trim() });
        }
        return links;
      }
    } catch {
      // Legacy text starting with "{" but not JSON -> falls back to plan B.
    }
  }
  // Legacy: free text — http(s) URLs separated by
  // spaces/commas/newlines are rescued.
  return value
    .split(/[\s,]+/)
    .filter((part) => isSafeUrl(part))
    .map((url) => ({ kind: kindFromUrl(url), url }));
}

/** Serializes social form to canonical JSON (keys with value). */
export function serializeSocialLinks(
  form: Partial<Record<SocialKind, string | null | undefined>>,
): string | null {
  const entries = Object.entries(form).filter(
    ([, url]) => typeof url === "string" && isSafeUrl(url.trim()),
  );
  if (entries.length === 0) {
    return null;
  }
  return JSON.stringify(Object.fromEntries(entries.map(([k, url]) => [k, url!.trim()])));
}
